import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { isPaidActive, isPaidExpired, platformLabel } from './users/entitlementState';

// Materialized paid entitlements read back through the is_web_admin-guarded
// admin_search_users RPC. The legacy user_subscriptions table is empty and is
// not written by the three-layer store model (#7); direct browser reads of
// user_entitlements/store_subscriptions are not permitted, so this goes
// through the deployed admin RPC instead. These are derived entitlement rows,
// not store-transaction lineage — the RPC also caps the scan, so the table
// presents a bounded result, not a complete subscription inventory.

const SCAN_LIMIT = 500;

interface EntitlementRow {
  user_id: string;
  email: string;
  display_name: string;
  username: string | null;
  entitlement_tier: string | null;
  entitlement_status: string | null;
  entitlement_platform: string | null;
  entitlement_period_end: string | null;
}

export const SubscriptionStatusTable: React.FC = () => {
  const [rows, setRows] = useState<EntitlementRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'active' | 'expired'>('all');
  const [scanBounded, setScanBounded] = useState(false);

  useEffect(() => {
    fetchEntitlements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const matchesFilter = (r: EntitlementRow) => {
    const fields = {
      entitlement_tier: r.entitlement_tier,
      entitlement_status: r.entitlement_status,
      entitlement_platform: r.entitlement_platform,
      entitlement_period_end: r.entitlement_period_end,
    };
    if (filter === 'active') return isPaidActive(fields);
    if (filter === 'expired') return isPaidExpired(fields);
    return true;
  };

  async function fetchEntitlements() {
    setLoading(true);
    setError(null);
    const { data, error: rpcError } = await supabase.rpc('admin_search_users', {
      p_query: null,
      p_limit: SCAN_LIMIT,
      p_offset: 0,
    });
    if (rpcError) {
      console.error('Error fetching entitlements:', rpcError);
      setError(rpcError.message || 'Failed to load entitlements');
      setRows([]);
      setScanBounded(false);
    } else {
      const scanned = (data || []) as Array<Record<string, any>>;
      // The RPC caps the scan; a full page is not proof the inventory ended.
      setScanBounded(scanned.length >= SCAN_LIMIT);
      const withEntitlement = scanned
        .filter((r) => r.entitlement_tier != null)
        .map((r) => ({
          user_id: r.id,
          email: r.email,
          display_name: r.display_name || '',
          username: r.username ?? null,
          entitlement_tier: r.entitlement_tier ?? null,
          entitlement_status: r.entitlement_status ?? null,
          entitlement_platform: r.entitlement_platform ?? null,
          entitlement_period_end: r.entitlement_period_end ?? null,
        }));
      setRows(withEntitlement);
    }
    setLoading(false);
  }

  const visible = rows.filter(matchesFilter);

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      'active': '#34C759',
      'expired': '#8E8E93',
      'grace_period': '#FF9500',
      'revoked': '#FF3B30',
      'cancelled': '#8E8E93',
      'none': '#8E8E93',
    };
    return colors[status] || '#8E8E93';
  };

  const getStatusBackground = (status: string) => {
    const backgrounds: Record<string, string> = {
      'active': 'rgba(52, 199, 89, 0.2)',
      'expired': 'rgba(142, 142, 147, 0.2)',
      'grace_period': 'rgba(255, 149, 0, 0.2)',
      'revoked': 'rgba(255, 59, 48, 0.2)',
      'cancelled': 'rgba(142, 142, 147, 0.2)',
      'none': 'rgba(142, 142, 147, 0.2)',
    };
    return backgrounds[status] || 'rgba(142, 142, 147, 0.2)';
  };

  const filterButton = (key: 'all' | 'active' | 'expired', label: string, activeColor: string) => (
    <button
      onClick={() => setFilter(key)}
      className={`px-3 py-1 text-sm rounded-lg border transition-all ${
        filter === key ? `${activeColor} text-white` : 'border-white/20 hover:bg-white/5'
      }`}
      style={filter !== key ? { color: 'var(--rp-text)' } : {}}
    >
      {label}
    </button>
  );

  return (
    <div className="rp-bento-card">
      <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
        <div>
          <h2 className="text-xl font-semibold" style={{ color: 'var(--rp-text)' }}>
            Materialized Entitlements
          </h2>
          <p className="text-xs mt-1" style={{ color: 'var(--rp-text-tertiary)' }}>
            Derived user_entitlements rows via the Admin read-back RPC — not store-transaction lineage. Admin grants are managed in Premium Management.
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          {filterButton('all', 'All', 'bg-blue-600 border-blue-600')}
          {filterButton('active', 'Active', 'bg-green-600 border-green-600')}
          {filterButton('expired', 'Expired', 'bg-gray-600 border-gray-600')}
          <button
            onClick={fetchEntitlements}
            disabled={loading}
            className="px-4 py-1 text-sm rounded-lg border border-white/20 hover:bg-white/5 transition-all disabled:opacity-50"
            style={{ color: 'var(--rp-text)' }}
          >
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin w-8 h-8 border-2 border-white/20 border-t-white rounded-full mx-auto mb-4"></div>
          <p style={{ color: 'var(--rp-text-secondary)' }}>Loading entitlements...</p>
        </div>
      ) : error ? (
        <div className="text-center py-12" style={{ color: 'var(--admin-error, #FF3B30)' }}>
          {error}
        </div>
      ) : visible.length === 0 ? (
        <div className="text-center py-12" style={{ color: 'var(--rp-text-tertiary)' }}>
          No entitlement rows within the scanned page{scanBounded ? ` (first ${SCAN_LIMIT} users — inventory may continue)` : ''}
        </div>
      ) : (
        <>
          {scanBounded && (
            <p className="text-xs mb-2 px-1" style={{ color: 'var(--rp-text-tertiary)' }}>
              Showing entitlements from the first {SCAN_LIMIT} users scanned — this is a bounded page, not the complete inventory.
            </p>
          )}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left py-3 px-4" style={{ color: 'var(--rp-text)' }}>User</th>
                <th className="text-left py-3 px-4" style={{ color: 'var(--rp-text)' }}>Tier</th>
                <th className="text-left py-3 px-4" style={{ color: 'var(--rp-text)' }}>Status</th>
                <th className="text-left py-3 px-4" style={{ color: 'var(--rp-text)' }}>Platform</th>
                <th className="text-left py-3 px-4" style={{ color: 'var(--rp-text)' }}>Period End</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((r) => (
                <tr key={r.user_id} className="border-b border-white/5 hover:bg-white/2 transition-colors">
                  <td className="py-3 px-4 text-sm" style={{ color: 'var(--rp-text)' }}>
                    {r.display_name || r.username || r.email}
                    <div className="text-xs font-mono" style={{ color: 'var(--rp-text-tertiary)' }}>{r.user_id.slice(0, 8)}…</div>
                  </td>
                  <td className="py-3 px-4 text-sm capitalize" style={{ color: 'var(--rp-text)' }}>
                    {r.entitlement_tier}
                  </td>
                  <td className="py-3 px-4">
                    <span
                      className="px-2 py-1 rounded-full text-xs font-medium"
                      style={{
                        backgroundColor: getStatusBackground(r.entitlement_status || 'none'),
                        color: getStatusColor(r.entitlement_status || 'none')
                      }}
                    >
                      {r.entitlement_status || 'none'}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm" style={{ color: 'var(--rp-text)' }}>
                    {platformLabel(r.entitlement_platform)}
                  </td>
                  <td className="py-3 px-4 text-sm" style={{ color: 'var(--rp-text)' }}>
                    {r.entitlement_period_end ? new Date(r.entitlement_period_end).toLocaleDateString() : 'None recorded'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}
    </div>
  );
};
